import { Component, OnInit, signal, ChangeDetectionStrategy, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { AuthService } from '../../core/services/auth.service';
import { ToastService } from '../../shared/services/toast.service';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { InputText } from '@openng/optimus-ui/inputtext';

@Component({
  selector: 'app-plugin-login',
  imports: [ReactiveFormsModule, ButtonDirective, InputText],
  changeDetection: ChangeDetectionStrategy.Default,
  templateUrl: './plugin-login.component.html'
})
export class PluginLoginComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly toastService = inject(ToastService);

  loginForm: FormGroup;
  isLoading = signal(false);
  errorMessage = '';

  readonly pluginVersion = signal<string>('2.0.0');

  constructor() {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]]
    });
  }

  ngOnInit() {
    const bridgeVersion = (window as any)?.angulerisPlugin?.version;
    if (bridgeVersion) {
      this.pluginVersion.set(bridgeVersion);
    }

    // Check if already authenticated
    if (this.authService.isAuthenticated()) {
      this.router.navigate(['/plugin/dashboard']);
    }
  }

  onSubmit() {
    if (this.loginForm.valid && !this.isLoading()) {
      this.isLoading.set(true);
      this.errorMessage = '';

      const { email, password } = this.loginForm.value;
      this.authService.login({ email, password }).subscribe({
        next: (response) => {
          if (response?.mustChangePassword) {
            this.router.navigate(['/change-password'], {
              queryParams: { returnUrl: '/plugin/dashboard' }
            });
            this.isLoading.set(false);
            return;
          }

          // Notify plugin about successful authentication
          this.notifyPlugin('AUTH_SUCCESS');

          // Navigate to dashboard
          this.router.navigate(['/plugin/dashboard']);
          this.isLoading.set(false);
        },
        error: (error) => {
          // Handle specific error messages for user status
          let errorMessage = 'Login failed. Please check your credentials.';
          
          if (error.message) {
            if (error.message.includes('deleted')) {
              errorMessage = 'Your account has been deleted. Please contact administrator.';
            } else if (error.message.includes('inactive')) {
              errorMessage = 'Your account is frozen. Please contact administrator.';
            } else {
              errorMessage = error.message;
            }
          }
          
          this.errorMessage = errorMessage;
          console.error('Login error:', error);
          this.isLoading.set(false);
        }
      });
    }
  }



  private notifyPlugin(message: string) {
    try {
      // Check if we're running inside WebView2
      if ((window as any).angulerisPlugin) {
        (window as any).angulerisPlugin.sendMessage(message);
      }
    } catch (error) {
      console.warn('Plugin communication not available:', error);
    }
  }

  getFieldError(fieldName: string): string {
    const field = this.loginForm.get(fieldName);
    if (field?.errors && field.touched) {
      if (field.errors['required']) {
        return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is required`;
      }
      if (field.errors['email']) {
        return 'Please enter a valid email address';
      }
      if (field.errors['minlength']) {
        return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} must be at least ${field.errors['minlength'].requiredLength} characters`;
      }
    }
    return '';
  }
}
